// The roulette wheel (casino.md §4.1 and §9).
//
// The wheel is ONE inline SVG. It is built one time for a pocket count,
// held in a memo, and turned by ONE custom property. Nothing here reads a
// frame, sends an intent or touches the DOM: the module answers with
// markup and with three pure figures, so `roulette.ts` and the tests read
// the same picture.
//
// **The picture is static and the two angles are the only variables.** The
// face, the 38 pockets and the ball all live inside the memoized string.
// The pocket reaches the browser as `--casino-ball-turn` and
// `--casino-wheel-turn` on the container, which `casino.css` reads in the
// `casino-ball-drop` and the `casino-wheel-drop` keyframe. Both figures
// come from ONE pocket, so the face rests where the called pocket stands
// under the ball (R988). The markup of the wheel therefore changes for the
// target alone, and the browser pays for two transforms
// (ui-map-interface.md §10.2).
//
// **Why the property and not a `transform` of its own.** `casino.css`
// already owns the two clocks — `--casino-wheel-ms` for the face and
// `--casino-ball-ms` for the ball — and both figures belong to
// `data/presentation/presentation.toml`. A `transition` written here
// would be a second owner of a duration the pack already owns. The
// container therefore carries geometry alone.
//
// **The SVG box is centred on 0,0.** An SVG element rotates about its
// own origin, and a group that must turn about the middle of the wheel
// would otherwise need a `transform-origin` rule this module may not
// write. `viewBox="-50 -50 100 100"` puts the middle at the origin, so
// `rotate()` turns about the hub with no rule at all.

// **How many whole turns the ball makes is a pack figure.**
// `CASINO_BALL_SPINS` of `data/presentation/presentation.toml` says how
// long the travel LOOKS, while `CASINO_BALL_MS` says how long the travel
// TAKES. No rule and no payout reads either one, and this module writes
// neither as a literal of its own.
//
// **The wheel stands for the spin alone** (R2117). In the betting window
// the roulette screen draws `lastPart` in its place: one line with the
// pocket the ball called last, so the number grid starts near the top of
// a telephone. The full wheel comes back when the window shuts.
//
// **The pocket numbers stay upright.** The face carries them, so a number
// at the bottom of a resting face read upside down. `casino.css` turns
// each `.pocket-n` back about its own centre by the face's own angle, on
// the face's own clock and curve, so the two turns cancel at every frame
// (R2117).

import { CASINO_BALL_SPINS } from '../../gen/presentation';
import { part } from './gameview';

/**
 * The pocket of the double zero on the wire (casino.md §4.1, R2752). A
 * pocket is one byte, and 37 is the first byte no number takes; the
 * reader sees it as 00.
 */
export const DOUBLE_ZERO = 37;

/**
 * The 38 pockets in the order of the American wheel, clockwise from the
 * zero (R2752). The order is the wheel's own and never the order of the
 * felt: the double zero stands opposite the zero, and every number stands
 * opposite the number after it.
 */
export const WHEEL_ORDER: readonly number[] = [
  0, 28, 9, 26, 30, 11, 7, 20, 32, 17, 5, 22, 34, 15, 3, 24, 36, 13, 1,
  DOUBLE_ZERO, 27, 10, 25, 29, 12, 8, 19, 31, 18, 6, 21, 33, 16, 4, 23, 35, 14, 2,
];

/** How a pocket reads: the double zero is 00, and every other pocket its number. */
export function pocketLabel(n: number): string {
  return n === DOUBLE_ZERO ? '00' : String(n);
}

/**
 * The red pockets of a classic wheel. Every other number from 1 to 36 is
 * black, and the zero is green. The set is the wheel's, not the felt's,
 * so the number grid of `roulette.ts` reads it from here.
 */
const RED = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);

/** What one pocket is: both zeros are green, and the rest are red or black. */
export function pocketColour(n: number): 'red' | 'black' | 'zero' {
  if (n === 0 || n === DOUBLE_ZERO) return 'zero';
  return RED.has(n) ? 'red' : 'black';
}

/**
 * The order of the pockets on a wheel of `pockets` places.
 *
 * The wheel of the game holds 38 places, which is what the pack states
 * and what `WHEEL_ORDER` names. A different count can only come from a
 * pack this client does not know, so the fallback is the plain counting
 * order: the picture stays whole and no pocket is lost.
 */
export function wheelOrder(pockets: number): readonly number[] {
  if (pockets === WHEEL_ORDER.length) return WHEEL_ORDER;
  const out: number[] = [];
  for (let i = 0; i < Math.max(1, pockets); i++) out.push(i);
  return out;
}

/**
 * Where one number sits on the wheel, in degrees clockwise from the top.
 *
 * The zero is at the top, so its angle is zero and every other pocket is
 * one step further round. A number no wheel holds answers zero, which
 * puts the ball on the hub mark and states nothing false.
 */
export function landingAngle(n: number, pockets: number): number {
  const order = wheelOrder(pockets);
  const i = order.indexOf(n);
  if (i < 0) return 0;
  return (i * 360) / order.length;
}

/**
 * The value of `--casino-ball-turn` for one called pocket.
 *
 * The ball turns anticlockwise while the face turns clockwise, which is
 * how a real wheel runs, so the answer is negative. It is a whole number
 * of turns away from the pocket's own angle, and therefore it ends
 * exactly over the pocket.
 *
 * Under reduced motion the caller passes `spins = 0`: the ball then sits
 * on the number at once and makes no travel at all (R541).
 */
export function ballTurn(
  n: number, pockets: number, spins = CASINO_BALL_SPINS,
): number {
  return round4(ballExact(n, pockets, spins));
}

/**
 * The ball's resting angle before the markup rounds it.
 *
 * `faceTurn` reads THIS and not the rounded figure: a rounding step is
 * five hundredths of a degree, and a value that fell that far under a
 * whole turn would send the face a whole turn back.
 */
function ballExact(n: number, pockets: number, spins: number): number {
  return landingAngle(n, pockets) / 360 - spins;
}

/**
 * The value of `--casino-wheel-turn`: where the FACE comes to rest.
 *
 * The face used to turn for ever, so the ball landed at the angle of its
 * pocket and the pocket itself had already moved on. A reader then watched
 * the ball sit beside a number the server did not call, which is a lie the
 * reader can see. The face therefore makes a FINITE travel of its own and
 * stops (R988).
 *
 * Where it stops is not free. A pocket stands at `landingAngle` in the
 * face's own frame, and the face carries it: the pocket's screen angle is
 * its own angle PLUS the face's rotation. The ball's screen angle is
 * `360 × ball`. The two must be the same angle, so the face rests at
 * `ball − landingAngle/360`, taken into one turn and set a whole number of
 * turns back.
 *
 * The face and the ball take the same count of whole turns, which
 * `CASINO_BALL_SPINS` states, and they take them in opposite directions:
 * the face over `CASINO_WHEEL_MS` and the ball over `CASINO_BALL_MS`. The
 * pack makes the two spans one span, so the ball comes to rest in its
 * pocket at the moment the face stops (R2117), and one pack figure serves
 * both turn counts.
 *
 * Under reduced motion the caller passes `spins = 0`: the face rests where
 * it started and the ball sits on its pocket at once (R541).
 */
export function faceTurn(
  n: number, pockets: number, spins = CASINO_BALL_SPINS,
): number {
  const want = ballExact(n, pockets, spins) - landingAngle(n, pockets) / 360;
  // Into one turn, so the face never unwinds a whole turn it has to make.
  const into = ((want % 1) + 1) % 1;
  return round4(spins + into);
}

/**
 * The pocket that stands under the ball when the two come to rest.
 *
 * It is the inverse of the pair above, and the one figure a test can read:
 * a picture that stops the ball beside the called number fails here and
 * not in a person's eye.
 */
export function pocketUnderBall(
  face: number, ball: number, pockets: number,
): number {
  const order = wheelOrder(pockets);
  const step = 360 / order.length;
  // The face carries its pockets, so the pocket under the ball is the one
  // whose OWN angle is the ball's screen angle less the face's rotation.
  const deg = (((ball - face) * 360) % 360 + 360) % 360;
  return order[Math.round(deg / step) % order.length];
}

/** Four decimals, which is finer than one pixel on any wheel this size. */
function round4(v: number): number {
  return Math.round(v * 10000) / 10000;
}

/**
 * The pocket the ball runs towards while the server has not called the
 * number yet.
 *
 * The wheel starts before the result frame arrives, so the ball needs a
 * place to travel to. The place is a pure function of the hand, so every
 * client of one table watches one picture and a re-render of the same
 * hand never moves the ball. The number is thrown away the moment the
 * result names the real pocket.
 */
export function pseudoPocket(handId: number, pockets: number): number {
  const order = wheelOrder(pockets);
  const i = Math.abs(Math.trunc(handId) * 13 + 5) % order.length;
  return order[i];
}

// --- the picture ------------------------------------------------------

/** The rim, the pockets, the numbers and the ball, in SVG units. */
const R_OUT = 48;
const R_IN = 30;
const R_TEXT = 39;
const R_BALL = 41.5;

/** One point on a circle, at an angle clockwise from the top. */
function pt(r: number, deg: number): string {
  const rad = (deg * Math.PI) / 180;
  const x = r * Math.sin(rad);
  const y = -r * Math.cos(rad);
  return `${round(x)} ${round(y)}`;
}

function round(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Every built face, by its pocket count. One build a session. */
const MEMO = new Map<number, string>();

/**
 * The whole wheel as one inline SVG.
 *
 * The answer is memoized, because the string is the same for the life of
 * the page: one build a pocket count, and a re-render at 4 Hz costs a map
 * lookup (§10.2). The caller states the angle on the container.
 *
 * Every hue is a token of `gen/tokens.css` (R362): a red pocket takes the
 * frozen `--casino-red`, a black pocket the deepest ink and each zero the
 * frozen `--casino-green`. A pocket is no verdict, so `--bad` and `--good`
 * stay with a loss and a gain (R2118). The module declares no colour of
 * its own.
 */
export function wheelSvg(pockets: number): string {
  const memo = MEMO.get(pockets);
  if (memo !== undefined) return memo;
  const order = wheelOrder(pockets);
  const step = 360 / order.length;
  const wedges: string[] = [];
  const marks: string[] = [];
  for (let i = 0; i < order.length; i++) {
    const n = order[i];
    const mid = i * step;
    const a0 = mid - step / 2;
    const a1 = mid + step / 2;
    const col = pocketColour(n);
    const fill = col === 'red' ? 'var(--casino-red)'
      : col === 'zero' ? 'var(--casino-green)' : 'rgb(var(--ink-rgb))';
    wedges.push(
      `<path class="pocket ${col}" fill="${fill}" d="M${pt(R_IN, a0)}`
      + `L${pt(R_OUT, a0)}A${R_OUT} ${R_OUT} 0 0 1 ${pt(R_OUT, a1)}`
      + `L${pt(R_IN, a1)}A${R_IN} ${R_IN} 0 0 0 ${pt(R_IN, a0)}Z"/>`,
    );
    const [tx, ty] = pt(R_TEXT, mid).split(' ');
    marks.push(
      `<text class="pocket-n" x="${tx}" y="${ty}" font-size="4.6"`
      + ' text-anchor="middle" dominant-baseline="central"'
      + ` fill="var(--shield)">${pocketLabel(n)}</text>`,
    );
  }
  const html = '<svg class="wheel-svg" viewBox="-50 -50 100 100"'
    + ' role="presentation" focusable="false">'
    // The face carries the pockets and the numbers, and `casino.css`
    // turns it for the whole time the wheel is on the screen.
    + `<g class="wheel-face">${wedges.join('')}${marks.join('')}`
    + `<circle class="hub" r="${R_IN}" fill="rgb(var(--sheet-rgb))"`
    + ' stroke="var(--edge)" stroke-width=".6"/></g>'
    // The rim stands still, so it does not belong to the face. The sheet
    // gives it the club ink (R2165), so a change of the club builds no
    // new wheel.
    + `<circle class="rim" r="${R_OUT}" fill="none" stroke="var(--edge)"`
    + ' stroke-width="1"/>'
    // The ball. It is drawn at the top, at the angle of the zero, and the
    // one property on the container carries it to the called pocket.
    + `<g class="wheel-ball"><circle r="2.4" cx="0" cy="-${R_BALL}"`
    + ' fill="var(--shield)"/></g>'
    + '</svg>';
  MEMO.set(pockets, html);
  return html;
}

/** What the caller states about one spin. */
export interface WheelState {
  /** How many pockets the wheel holds. The pack states it. */
  pockets: number;
  /** The called pocket, or null while the server has not called one. */
  number: number | null;
  /** The hand, which fixes the pocket the ball runs at before the call. */
  handId: number;
  /** True under reduced motion: the ball sits on the number at once. */
  still: boolean;
}

/**
 * The class and the inline property the container carries.
 *
 * `.casino-wheel` is what `casino.css` styles and `.wheel` is the hook the
 * panel publishes, so the container wears both. The inline property is a
 * geometric value that changes per spin, which is the one thing a module
 * may write inline.
 */
function wheelAttrs(s: WheelState): string {
  return `class="casino-wheel wheel${s.still ? ' still' : ''}" style="${wheelStyle(s)}"`;
}

/**
 * The two angles the container carries for one state of the wheel.
 *
 * The face and the ball rest on ONE pocket, so the two figures are built
 * together and from one pocket. A caller that wrote one of them alone
 * could land the ball beside the number the server called.
 */
function wheelStyle(s: WheelState): string {
  const pocket = s.number ?? pseudoPocket(s.handId, s.pockets);
  const spins = s.still ? 0 : CASINO_BALL_SPINS;
  return `--casino-ball-turn:${ballTurn(pocket, s.pockets, spins)}turn`
    + `;--casino-wheel-turn:${faceTurn(pocket, s.pockets, spins)}turn`;
}

/**
 * The wheel folded to one line, for the betting window (R2117).
 *
 * It states the pocket the ball called last: the word LAST, the number on
 * a chip in the pocket's colour, and the colour word, because a reader
 * who sees no colour must still read red, black or the zero. The part is
 * one line tall, so the number grid starts under the chip row and the
 * kind row. `word` is the colour word the caller reads from the strings.
 */
export function lastPart(n: number, word: string, label: string): string {
  const col = pocketColour(n);
  return part('last', `class="casino-last ${col}"`,
    `<span class="lbl">${label}</span><span class="pk">${pocketLabel(n)}</span>`
    + `<span class="word">${word}</span>`);
}

/** One casino seat the rim of the wheel shows (R2165). */
export interface RimSeat {
  /** The seat number, from 0. It names the bearing of the seat. */
  seat: number;
  /** The name, already escaped. */
  name: string;
  /** The badge markup of the seat's faction, or '' for a fictive seat. */
  badge: string;
  /** What the seat staked on this spin, as text, or '' for no stake. */
  stake: string;
  /** The reader's own seat. */
  you: boolean;
}

/**
 * The bearing of one seat of `count` round the wheel, in degrees from the
 * top. The seats stand half a step off the top, so the top stays free for
 * the index of the ball and the seats stand in two columns of three
 * beside the wheel at six seats.
 */
export function seatBearing(seat: number, count: number): number {
  const n = Math.max(1, count);
  return round(((seat + 0.5) * 360) / n);
}

/**
 * The casino seats on the rim of the wheel and the club badge at its hub,
 * as ONE PART beside the wheel (R2165, card 02 of mockup round 101): the
 * reader sees the other seats and what each staked while the ball runs.
 * The part stands over the wheel in one grid cell (`.wheel-stage`), so a
 * seat that comes or goes rewrites this part and never the wheel, whose
 * travel must run to its end (`gameview.ts`, rule 4). A bearing is
 * geometry, so it rides inline as `--b`, and the sheet places the seat.
 */
export function rimPart(seats: readonly RimSeat[], count: number, hubBadge: string): string {
  const taken = new Map(seats.map((s) => [s.seat, s]));
  let out = '';
  for (let i = 0; i < Math.max(count, 1); i++) {
    const s = taken.get(i);
    const b = `style="--b:${seatBearing(i, count)}deg"`;
    if (!s) {
      out += `<span class="rim-seat empty" ${b} aria-hidden="true"></span>`;
      continue;
    }
    out += `<span class="rim-seat${s.you ? ' you' : ''}" ${b}>`
      + `<span class="rs-n">${s.badge}<span class="rs-name">${s.name}</span></span>`
      + (s.stake ? `<span class="rs-s">${s.stake}</span>` : '')
      + '</span>';
  }
  const hub = hubBadge ? `<span class="hub-badge" aria-hidden="true">${hubBadge}</span>` : '';
  return part('rim', 'class="wheel-rim"', out + hub);
}

/**
 * The same wheel as ONE PART of a screen (`gameview.ts`).
 *
 * The panel patches per part, and this part is the one the roulette
 * screen must never rewrite while the ball travels. The markup therefore
 * carries the pocket count, the called pocket and the hand, and nothing
 * that steps with a clock: the window countdown and the called row are
 * parts of their own, so a second that passes moves no element here
 * (R988).
 */
export function wheelPart(s: WheelState): string {
  return part('wheel', wheelAttrs(s), wheelSvg(s.pockets));
}
